import React from 'react';
import { formatDateIST } from '../../utils/formatIST';

/**
 * ConsentBlock (CONSENT-CAPTURE P1) — ONE compact checkbox row, shared by
 * Register Visit, Register Admission (Step 2), the inline register-patient
 * dialog and appointment check-in. Deliberately NOT the collapsible
 * Variant 3 card from the mockups — the owner cut that for P1 (one consent
 * doesn't need collapse machinery; it returns in P2 with several).
 *
 * DENSITY (owner, live click-through) — no card chrome (no heading, no
 * border/padding box): checkbox + declaration + chip on ONE line where
 * width allows, wrapping only on narrow viewports; the staff instruction
 * sits directly under it in small secondary text, not a separately padded
 * paragraph. This is what buys back the Variant-3 collapse's compactness
 * without a collapse interaction.
 *
 * TWO STRINGS, TWO AUDIENCES — NEVER MERGED:
 *  - `catalogEntry.text` is the PATIENT's declaration, owned and versioned by
 *    the server (`GET /api/organisation/settings/consents`). Rendered
 *    verbatim, in full — never hardcoded here, never truncated/ellipsized —
 *    so it can never drift from the `version` the server stamps on the write.
 *  - `STAFF_CONSENT_INSTRUCTION` below is FRONTEND-ONLY chrome for whoever is
 *    at the desk (receptionist / nurse / admissions clerk — worded
 *    role-neutral on purpose, INTAKE.md D12). It is rendered in a visibly
 *    distinct (small, secondary) style, is NEVER sent to the server and
 *    NEVER part of any stored value — callers must build the write payload
 *    from `checked` alone.
 *
 * D13 (mandatory consent): this component only renders the tick + the chip;
 * the disabled-primary-action + its reason line live in each HOST form
 * (beside that form's own submit button), never inside this card — per the
 * owner's rule, unticking must not read as an error on the consent row
 * itself.
 */

// Frontend-only staff instruction — deliberately role-neutral (the same card
// renders on the admission desk and the inline dialog). Never sent to the
// server, never stored, never versioned.
//
// D15.2 (owner ruling, compliance-gate item 15/N5) — the second clause ("They
// may say no — it will not affect their treatment.") is DELETED. Under D13
// it is FALSE: refusing disables Register/Admit/Check-In and the server
// 422s the create, so the patient is NOT registered and therefore not
// treated there. The false promise was an Indian Contract Act s.14
// misrepresentation exposure independent of D13. Do not re-add it.
export const STAFF_CONSENT_INSTRUCTION =
  'Please read this aloud to the patient before ticking.';

/**
 * Single source of truth for "should the box render ticked" — existing
 * patient record wins; otherwise the server's catalogue default. Never an FE
 * literal (D6).
 *
 * D15.1 (owner ruling, compliance-gate BLOCKER) — when NEITHER an existing
 * patient record NOR the catalogue is available, this MUST return `false`,
 * never `true`. A `true` fallback here is exactly the defect this feature
 * exists to prevent: the server would stamp `granted:true` for a
 * declaration the card never displayed (the checkbox stays behind a
 * skeleton — see ConsentBlock below). Every host additionally holds its
 * primary action disabled while `catalogEntry` is null (independent of this
 * return value), so a catalogue outage blocks the front desk rather than
 * fabricate a record.
 */
export function resolveConsentGranted(existingEntry, catalogEntry) {
  if (existingEntry) return !!existingEntry.granted;
  if (catalogEntry) return !!catalogEntry.default_granted;
  return false;
}

function formatConsentDate(iso) {
  return formatDateIST(iso);
}

export default function ConsentBlock({
  catalogEntry,       // { key, label, text, default_granted } | null (still loading)
  checked,
  onChange,
  existingEntry,      // { granted, at } | null/undefined — "not asked yet"
  patientSelected = true,
  disabledMessage,    // shown instead of the row when !patientSelected
}) {
  if (!patientSelected) {
    return (
      <div
        className="mb-2 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 px-2 py-1.5 opacity-70 text-[11.5px] text-slate-500 dark:text-slate-400"
        data-testid="consent-block-disabled"
      >
        {disabledMessage}
      </div>
    );
  }

  // Owner (live click-through) — the "Not asked yet" / "On file · <date>"
  // chip carries the state in ONE line; the explanatory paragraph that used
  // to sit above it (LEGACY_CONSENT_BANNER_TEXT) is REMOVED. The chip is not
  // a decoration — it is the honest-record signal (absent ≠ granted) — so it
  // stays even though the paragraph is gone.
  const dateSuffix = existingEntry?.at ? ` · ${formatConsentDate(existingEntry.at)}` : '';
  const chip = existingEntry
    ? (existingEntry.granted
      ? { label: `On file${dateSuffix}`, cls: 'border-teal-400 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200' }
      : { label: `Declined${dateSuffix}`, cls: 'border-red-300 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200' })
    : { label: 'Not asked yet', cls: 'border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300' };

  if (!catalogEntry) {
    return (
      <div className="mb-2" data-testid="consent-block">
        <div className="h-8 rounded-md bg-slate-200 dark:bg-slate-700 animate-pulse" data-testid="consent-skeleton" />
      </div>
    );
  }

  // Owner: once consent is ON FILE the tick is locked and the read-aloud line
  // goes — you do not re-read the declaration to someone who already agreed,
  // and an accidental untick should not be possible.
  //
  // ONLY when it is on file as GRANTED. A patient recorded as DECLINED must
  // stay editable: under D13 an unticked box blocks registration, so locking a
  // declined patient would make them permanently unregisterable at every desk.
  // "Not asked yet" stays editable for the obvious reason.
  // ...and ONLY while the tick still agrees with the record. If it is unticked
  // the operator has deliberately moved away from what is on file, and locking
  // there would strand them: the box would be disabled in a state that blocks
  // submission, with no way back. Found by QA racing a late patient fetch
  // against an untick — the refusal was silently reverted AND frozen.
  const lockedOnFile = existingEntry?.granted === true && checked === true;

  return (
    <div className="mb-2" data-testid="consent-block">
      <label
        className={`flex items-start gap-2 flex-wrap ${lockedOnFile ? 'cursor-default' : 'cursor-pointer'}`}
        data-testid="consent-checkbox-row"
      >
        <input
          type="checkbox"
          className={`h-4 w-4 mt-0.5 shrink-0 accent-teal-600 ${lockedOnFile ? 'opacity-60 cursor-not-allowed' : ''}`}
          checked={checked}
          disabled={lockedOnFile}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={catalogEntry.label}
          data-testid="consent-checkbox"
        />
        <span className="flex-1 min-w-[200px] text-[12px] leading-snug text-slate-700 dark:text-slate-200" data-testid="consent-declaration">
          {catalogEntry.text}
          {' '}
          <span
            className={`inline-block align-middle text-[10.5px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${chip.cls}`}
            data-testid="consent-chip"
          >
            {chip.label}
          </span>
        </span>
      </label>
      {!lockedOnFile && (
        <div
          className="ml-6 mt-0.5 text-[10.5px] italic text-slate-500 dark:text-slate-400"
          data-testid="consent-staff-instruction"
        >
          {STAFF_CONSENT_INSTRUCTION}
        </div>
      )}
    </div>
  );
}
